import * as path from 'node:path';
import { createHash } from 'node:crypto';
import type { ActiveAbility } from './types.js';
import { buildSkillMd, buildAgentMd } from './synthesize.js';
import { ABILITY_GUIDE_FILENAME } from './materialize.js';

/**
 * Ability Format v2 (P4a) — typed, content-addressed parts. A part is the
 * atom of the format: its hash is a digest over (kind, slug, sorted disk
 * file map). P4a mints skill / agent / files parts; mcp / policy / lessons /
 * settings join in later phases (the union is the format's vocabulary).
 */
export type PartKind = 'skill' | 'agent' | 'files' | 'mcp' | 'policy' | 'lessons' | 'settings';

export interface PartRef {
  kind: PartKind;
  slug: string;
  /** 'sha256:<64 hex>' */
  hash: string;
}

/** Stable roster key for a part: `<kind>:<slug>`. The files part is `files:root`. */
export function partKeyOf(ref: Pick<PartRef, 'kind' | 'slug'>): string {
  return `${ref.kind}:${ref.slug}`;
}

/**
 * Content hash of one part. Deterministic and order-independent: file map
 * entries are digested as sorted `<sha256(path)>\n<sha256(content)>` lines under
 * a `part:<kind>:<slug>` header. fileMap values are raw file CONTENT.
 *
 * The PATH is hashed (not concatenated raw): a path containing the `\n`
 * delimiter would otherwise blur the record boundary — e.g. `{a:x, b:y}` and a
 * single key `` `a\n${sha256(x)}\nfile:b` `` produced an identical stream under
 * naive concatenation. Both path and content enter as fixed-width, delimiter-
 * free hex, so no record can bleed into the next.
 *
 * Storage-backed assets (archive/CAS + single-file) fold in AFTER the file map,
 * under a distinct `asset:` prefix, digested to the same fixed-width hex (so an
 * asset record can no more bleed than a file one). Folding them here keeps the
 * per-part hash consistent with the whole-disc `computeChecksum` (which also
 * folds assets): an asset-only change — a new content-addressed archive key with
 * a byte-identical SKILL.md/inline body — flips this part so `planPartRepairs`
 * routes it through parts-repair and the archive is re-unpacked. Part hashes are
 * only ever computed from the FEED (never recomputed from disk), so folding a
 * key that isn't on disk cannot fabricate a false tamper. Absent/empty assets →
 * zero iterations → byte-identical to the pre-asset hash.
 */
export function computePartHash(
  kind: PartKind,
  slug: string,
  fileMap: Record<string, string>,
  assets?: ReadonlyArray<{ target_path: string; key: string }>,
): string {
  const h = createHash('sha256');
  h.update(`part:${kind}:${slug}`);
  for (const p of Object.keys(fileMap).sort()) {
    h.update(`\nfile:${createHash('sha256').update(p).digest('hex')}\n`);
    h.update(createHash('sha256').update(fileMap[p]).digest('hex'));
  }
  for (const a of [...(assets ?? [])].sort((x, y) => x.key.localeCompare(y.key))) {
    h.update(`\nasset:${createHash('sha256').update(a.target_path).digest('hex')}\n`);
    h.update(createHash('sha256').update(a.key).digest('hex'));
  }
  return `sha256:${h.digest('hex')}`;
}

/**
 * The disk-relative file map for ONE part — exactly the files the
 * materializer writes for it (same synthesis, same path mapping), so a part
 * hash computed from the feed matches a hash computed from what lands on
 * disk. Returns null for a slug the ability doesn't carry.
 */
export function partFileMap(
  active: ActiveAbility,
  ref: Pick<PartRef, 'kind' | 'slug'>,
): Record<string, string> | null {
  if (ref.kind === 'skill') {
    const s = active.skills.find((x) => x.slug === ref.slug);
    if (!s) return null;
    const map: Record<string, string> = {
      [path.posix.join('skills', s.slug, 'SKILL.md')]: buildSkillMd(s),
    };
    for (const f of s.files) {
      if (f.path === 'SKILL.md' || f.content === null) continue;
      map[path.posix.join('skills', s.slug, f.path)] = f.content;
    }
    return map;
  }
  if (ref.kind === 'agent') {
    const a = active.agents.find((x) => x.slug === ref.slug);
    if (!a) return null;
    return { [path.posix.join('agents', `${a.slug}.md`)]: buildAgentMd(a) };
  }
  if (ref.kind === 'files' && ref.slug === 'root') {
    const map: Record<string, string> = {};
    for (const f of active.files) {
      if (f.is_binary || f.content === null) continue;
      map[f.path === 'CLAUDE.md' ? ABILITY_GUIDE_FILENAME : f.path] = f.content;
    }
    return Object.keys(map).length > 0 ? map : null;
  }
  return null;
}

/** Every part this ability carries, hashed. Order: files, skills (slug-sorted), agents (slug-sorted). */
export function buildPartsIndex(active: ActiveAbility): PartRef[] {
  const parts: PartRef[] = [];
  const filesMap = partFileMap(active, { kind: 'files', slug: 'root' });
  if (filesMap) parts.push({ kind: 'files', slug: 'root', hash: computePartHash('files', 'root', filesMap) });
  for (const s of [...active.skills].sort((a, b) => a.slug.localeCompare(b.slug))) {
    const m = partFileMap(active, { kind: 'skill', slug: s.slug });
    // Fold the skill's storage_assets into its part hash (partFileMap stays inline-
    // files only — it's also the materializer's write map). An asset-only change
    // then lands the skill in planPartRepairs' `changed` set and re-unpacks.
    if (m) parts.push({ kind: 'skill', slug: s.slug, hash: computePartHash('skill', s.slug, m, s.storage_assets) });
  }
  for (const a of [...active.agents].sort((x, y) => x.slug.localeCompare(y.slug))) {
    const m = partFileMap(active, { kind: 'agent', slug: a.slug });
    if (m) parts.push({ kind: 'agent', slug: a.slug, hash: computePartHash('agent', a.slug, m) });
  }
  return parts;
}
